// The Quote Brain's journey line (lucid 019 O1): Files, Summary, Evidence, Scope, Pricing, Ready, drawn from the model in
// src/services/quote-brain-journey.ts. The step under way spins and its link moves (a count fills it, otherwise a band runs
// along it), and the node pulses once each time something real happens (a file read, a turn of the run finished). With
// nothing working it holds still. Reduced motion keeps the states and drops the movement (quote-brain.css).

import React, { useEffect, useRef, useState } from 'react';
import { Icons } from '../ui/Icon';
import { JOURNEY_STEPS, type JourneyModel } from '../../services/quote-brain-journey';

export function QuoteBrainJourney({ model }: { model: JourneyModel }) {
    // One pulse per real event, never on the first draw.
    const [pulse, setPulse] = useState(0);
    const firstBeat = useRef(model.beat);
    useEffect(() => {
        if (model.beat === firstBeat.current) return;
        firstBeat.current = '';
        setPulse((n) => n + 1);
    }, [model.beat]);

    const last = JOURNEY_STEPS.length - 1;
    return (
        <ol
            className="qb-journey"
            aria-label="Quote progress"
            data-testid="quote-brain-journey"
            data-moving={model.moving ? 'true' : 'false'}
            data-done={model.done ? 'true' : 'false'}
        >
            {JOURNEY_STEPS.map((step, index) => {
                const state = model.done || index < model.active ? 'done' : index === model.active ? 'active' : 'waiting';
                const linkFill = state === 'done' ? 1 : state === 'active' ? model.fill : 0;
                return (
                    <li
                        key={step.id}
                        className="qb-journey__step"
                        data-step={step.id}
                        data-state={state}
                        aria-current={state === 'active' ? 'step' : undefined}
                    >
                        <span className="qb-journey__track">
                            <span className="qb-journey__node">
                                {state === 'done' && <Icons.Check size={9} strokeWidth={3} aria-hidden="true" />}
                                {state === 'active' && model.moving && pulse > 0 && <span key={pulse} className="qb-journey__pulse" aria-hidden="true" />}
                            </span>
                            {index < last && (
                                <span className="qb-journey__link" data-counted={state === 'active' && model.fill != null ? 'true' : undefined}>
                                    {linkFill != null && linkFill > 0 && (
                                        <span className="qb-journey__fill" style={{ width: `${Math.round(linkFill * 100)}%` }} />
                                    )}
                                </span>
                            )}
                        </span>
                        <span className="qb-journey__label">
                            {step.label}
                            <span className="sr-only">{state === 'done' ? ', done' : state === 'active' ? (model.moving ? ', under way' : ', next') : ''}</span>
                        </span>
                    </li>
                );
            })}
        </ol>
    );
}

export default QuoteBrainJourney;
